const EXPIRED_TIME_KEY = '_expiredTime'
const EXPIRED_FLAG_KEY = '_isExpired'

/**
 * `mousemove` alone misses the two ways a prescriber commonly spends minutes on
 * a page without moving the pointer: scrolling a long list with the wheel while
 * the mouse rests, and waiting on the eRx round trip after Submit. Pointer and
 * wheel events cover both. `scroll` is registered on the document in the
 * capture phase because scroll events from an inner container (every Chakra
 * modal body) do not bubble to `window`.
 */
const WINDOW_ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'pointerdown',
  'click',
  'wheel',
  'keydown',
  'touchstart',
] as const

/** Writing the deadline on every mousemove is wasteful; once a second is plenty. */
const MIN_WRITE_INTERVAL_MS = 1000

const CHECK_INTERVAL_MS = 1000

export const readIdleDeadline = (): number =>
  parseInt(localStorage.getItem(EXPIRED_TIME_KEY) || '0', 10)

/**
 * Drops the persisted idle deadline. Must be called wherever a session starts,
 * because the deadline outlives the session it was written for: a user who
 * returns to a deadline that already elapsed would otherwise be signed straight
 * back out by the timer the moment it is next constructed, which reads as
 * "I had to log in twice".
 */
export const resetIdleDeadline = (): void => {
  localStorage.removeItem(EXPIRED_TIME_KEY)
  localStorage.removeItem(EXPIRED_FLAG_KEY)
}

class IdleTimer {
  interval?: number
  onTimeout?: () => void
  timeoutInMs: number
  private lastWriteAt = 0
  /**
   * The deadline value already reported to `onTimeout`, so an elapsed deadline
   * is reported once rather than every tick. Deliberately not a permanent
   * latch: `App` declines the timeout when there is no session to end, and the
   * instance has to stay usable for whatever session starts next. Cleared as
   * soon as the deadline is reset or moves into the future.
   */
  private reportedDeadline: number | null = null

  constructor({
    onTimeout: customizeOnTimeout,
    timeoutInMs,
  }: {
    onTimeout?: () => void
    timeoutInMs: number
  }) {
    this.onTimeout = customizeOnTimeout
    this.timeoutInMs = timeoutInMs
    this.handleActivity = this.handleActivity.bind(this)
    this.checkDeadline = this.checkDeadline.bind(this)

    // Register and start before evaluating the deadline. The previous order
    // fired the timeout from the constructor and returned without ever arming
    // the tracker, so a timeout the caller declined to act on left the session
    // with no idle protection for the rest of the page load.
    this.registerTracker()
    this.interval = window.setInterval(this.checkDeadline, CHECK_INTERVAL_MS)
    this.checkDeadline()
  }

  private checkDeadline() {
    const deadline = readIdleDeadline()
    if (!deadline) {
      // Nothing persisted yet, or a session that just reset it. Arm the
      // deadline here rather than waiting for the first activity event, so a
      // user who signs in and walks away still times out. This is also how an
      // instance whose timeout was declined picks up the next session.
      this.reportedDeadline = null
      this.writeDeadline()
      return
    }

    if (Date.now() <= deadline) {
      this.reportedDeadline = null
      return
    }

    if (this.reportedDeadline === deadline) return

    this.reportedDeadline = deadline
    localStorage.setItem(EXPIRED_FLAG_KEY, 'true')
    this.onTimeout && this.onTimeout()
  }

  private handleActivity() {
    // Once an elapsed deadline has been reported, a stray event must not push
    // it back into the future and cancel a sign-out that is already under way.
    // Recovery goes through resetIdleDeadline, not through activity.
    if (this.reportedDeadline !== null) return
    if (Date.now() - this.lastWriteAt < MIN_WRITE_INTERVAL_MS) return
    this.writeDeadline()
  }

  private writeDeadline() {
    this.lastWriteAt = Date.now()
    localStorage.setItem(EXPIRED_TIME_KEY, (this.lastWriteAt + this.timeoutInMs).toString())
  }

  registerTracker() {
    WINDOW_ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, this.handleActivity))
    document.addEventListener('scroll', this.handleActivity, true)
  }

  cleanUpTracker() {
    window.clearInterval(this.interval)
    this.interval = undefined
    WINDOW_ACTIVITY_EVENTS.forEach((event) =>
      window.removeEventListener(event, this.handleActivity)
    )
    document.removeEventListener('scroll', this.handleActivity, true)
  }
}

export default IdleTimer
